import { Subject, Observable } from 'rxjs';
import { SignalRConnectionManager } from './signalr-connection.manager';
import { Logger } from '../../../../core/utilities/logging/logger';
import { IHubEvent } from '../models/hub-event.model';
import { IHubRequirnments } from '../models/hub-requirnments.model';

export abstract class SignalRHub {
    private connectionManager: SignalRConnectionManager;
    private eventSubjects: Map<string, Subject<any>> = new Map();
    connectionStatus$: Observable<boolean>;

    protected constructor({ connectionManager, collectionUrl, hubEvents: events }: IHubRequirnments) {
        this.connectionManager = connectionManager;
        this.connectionStatus$ = connectionManager.connectionStatus$;
        this.connectionManager.connectToHub(collectionUrl).subscribe({
            next: () => {
                Logger.debug('SignalR connected');
                this.registerEvents(events);
            },
            error: err => Logger.warn(`SignalR connection error: ${err}`)
        });
    }

    private registerEvents<T>(messages: Array<IHubEvent<T>>): void {
        messages.forEach(message => {
            if (this.connectionManager.isConnected()) {
                this.onEvent(message.name).subscribe({
                    next: (data: any) => message.subject.next(data),
                    error: err => {
                        Logger.warn(`SignalR connection error: ${err}`);
                    }
                });
            }
        });
    }

    private onEvent<T>(eventName: string): Observable<T> {
        if (!this.connectionManager.isConnected()) {
            return new Observable(observer => observer.error('Connection not established.'));
        }

        if (!this.eventSubjects.has(eventName)) {
            this.eventSubjects.set(eventName, new Subject<T>());
        }

        const eventSubject = this.eventSubjects.get(eventName);
        this.connectionManager.connection?.on(eventName, (message: T) => {
            eventSubject?.next(message);
        });

        return eventSubject?.asObservable() as Observable<T>;
    }

    invokeMethod<TResponse>(methodName: string, ...args: any[]): Observable<TResponse> {
        if (this.connectionManager.isConnected()) {
            return new Observable<TResponse>(observer => {
                this.connectionManager.connection
                    ?.invoke(methodName, ...args)
                    .then(response => {
                        Logger.debug(`Successfully invoked method: ${methodName}`);
                        observer.next(response as TResponse);
                        observer.complete();
                    })
                    .catch(err => {
                        Logger.warn(`Error while invoking method: ${methodName}`, err);
                        observer.error(err);
                    });
            });
        }

        return new Observable<TResponse>(observer => observer.error('Connection not established.'));
    }
}
